import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import {
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Alert,
  Button,
  Typography,
} from "@mui/material";
import { useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { z } from "zod";

import { useSubscribeAnnouncementsMutation } from "api/public/subscribe/subscribe.api";
import { useAppForm } from "components/Forms/AppForm";

const emailRegex = /^[\w.%+-]+@[\w.-]+\.[\w]{2,}$/;
const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const signupSchema = z.object({
  email: z
    .string()
    .min(1, "Please enter a valid email")
    .regex(emailRegex, "Please enter a valid email"),
  name: z.string().min(1, "Please enter a name"),
});

const defaultValues = {
  email: "",
  name: "",
};

export const SignupCard = () => {
  const [subscribeAnnouncements, { isLoading }] =
    useSubscribeAnnouncementsMutation();

  const [recaptcha_token, setToken] = useState<string | null>(null);
  const [signupStatus, setSignupStatus] = useState<
    "initial" | "success" | "error"
  >("initial");
  const [errorMessage, setErrorMessage] = useState<string>();

  const form = useAppForm({
    defaultValues,
    validators: {
      onBlur: signupSchema,
    },
    onSubmit: async ({ value }) => {
      if (!recaptcha_token) {
        return;
      }

      try {
        await subscribeAnnouncements({
          email: value.email,
          name: value.name,
          recaptcha_token,
        }).unwrap();

        setSignupStatus("success");
      } catch (error: unknown) {
        const message =
          (error as { data?: { error?: string } })?.data?.error ||
          "Could not complete subscription.";
        setSignupStatus("error");
        setErrorMessage(message);
      }
    },
  });

  if (signupStatus === "success") return <SignupSuccess />;

  return (
    <Card sx={{ margin: "1em auto", width: "fit-content" }} elevation={3}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <CardHeader
          component="h5"
          title="Get Email Updates"
          sx={{ marginBottom: 0 }}
        />

        <CardContent sx={{ padding: "0 2em" }}>
          <Typography variant="body1">
            Want to stay up to date with the latest announcements?
            <br />
            Subscribe now to receive email alerts whenever new updates are
            posted!
          </Typography>
          <form.AppField name="name">
            {(field) => (
              <field.TextInput
                label="Enter name"
                sx={{ width: "100%", mt: 2 }}
              />
            )}
          </form.AppField>

          <form.AppField name="email">
            {(field) => (
              <field.TextInput
                label="Enter email"
                placeholder="example@niap-ccevs.org"
                type="email"
                sx={{ width: "100%", mt: 2 }}
              />
            )}
          </form.AppField>

          {signupStatus === "error" && (
            <Alert variant="standard" severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </CardContent>

        <CardActions
          sx={{ justifyContent: "space-around", padding: "1em 0em" }}
        >
          <ReCAPTCHA
            id="announcements-recaptcha"
            sitekey={siteKey}
            onChange={(e) => setToken(e)}
            onExpired={() => setToken(null)}
          />
          <form.Subscribe selector={(state) => state.canSubmit}>
            {(canSubmit) => (
              <Button
                type="submit"
                variant="contained"
                sx={{ color: "white", padding: ".75em", width: "150px" }}
                disabled={!canSubmit || !recaptcha_token || isLoading}
              >
                Subscribe
              </Button>
            )}
          </form.Subscribe>
        </CardActions>
      </form>
    </Card>
  );
};

const SignupSuccess = () => {
  return (
    <Card sx={{ margin: "1em auto", width: "fit-content" }} elevation={3}>
      <CardHeader
        avatar={<MarkEmailReadIcon color="success" fontSize="large" />}
        title="Thanks for subscribing!"
        titleTypographyProps={{ variant: "h5" }}
        sx={{ marginBottom: 0 }}
      />

      <CardContent sx={{ padding: "0 2em", margin: 0 }}>
        <Typography variant="body1" sx={{ m: 0 }}>
          We&apos;ll keep you posted with the latest updates, news, and
          announcements!
        </Typography>
      </CardContent>
    </Card>
  );
};
